import { useStore } from '@tanstack/react-form'
import { useMutation } from '@tanstack/react-query'
import { LakehouseBigQueryConnection, LakehouseConnectionTypeEnum } from '@vendia/management-api-types'
import { GetLakehouseTablesDocument } from '@vendia/control-plane-api'
import debug from 'debug'
import { useNavigate } from 'react-router'
import Card from 'src/components/containers/card'
import TextField from 'src/components/fields/text.field'
import { ScrollableStepContent } from 'src/components/flows/scrollable-step-content'
import StepButtons from 'src/components/flows/step-buttons'
import { StepContentHeader } from 'src/components/flows/step-header'
import { StepWrapper } from 'src/components/flows/step-wrapper'
import { StepComponent } from 'src/components/flows/types'
import { createOnChangeValidator, isRequiredValidator } from 'src/utils/form/validation'
import useControlPlaneApi from 'src/utils/hooks/use-control-plane-api'
import { useFirstFocus } from 'src/utils/hooks/use-first-focus'
import { LakehouseFlowStepValues } from 'src/utils/lakehouse/types'
import { lakehouseFlowFieldValidator } from 'src/utils/lakehouse/validators'
import notify from 'src/utils/notify'

import { StepId } from '../config'

const logger = debug('app:BigQuery')

export const StepConnectBigQuery: StepComponent<LakehouseFlowStepValues> = ({ context }) => {
  const form = context.form
  const client = useControlPlaneApi()
  const navigate = useNavigate()
  const nameRef = useFirstFocus()
  const prefix = 'source.bigquery'

  const { uniName, nodeName } = useStore(form.store, (s) => s.values)
  const connection = useStore(form.store, (s) => s.values.source.connection)

  const bigquery = useStore(form.store, (s) => s.values.source?.bigquery)

  const getTablesMutation = useMutation({
    mutationFn: () =>
      client.graphqlFetch({ query: GetLakehouseTablesDocument, variables: {
        uniName: uniName!,
        nodeName: nodeName!,
        connectionInput: {
          type: LakehouseConnectionTypeEnum.Bigquery,
          bigqueryConnection: {
            ...(bigquery as LakehouseBigQueryConnection),
            privateKeyId: bigquery?.privateKeyId as string,
            clientId: bigquery?.clientId as string,
            clientX509CertUrl: bigquery?.clientX509CertUrl as string,
          },
        },
      } }),
    onError: (error) => notify.error(`Error testing BigQuery connection: ${error}`),
    onSuccess: (response) => {
      logger('bigquery connection response', response)
      if (response.errors) {
        notify.error(`${response.errors[0].message}`)
        return
      }
      if (!response.getLakehouseTables) {
        notify.error('Error testing BigQuery connection')
        return
      }
      form.setFieldValue('source.availableTables', response.getLakehouseTables.tables as string[])
      form.setFieldValue('source.sourceRegion', response.getLakehouseTables.region as string)
      context.goToStep(StepId.SelectTable)
    },
  })

  const parseJsonKeyFile = (event: React.FocusEvent<HTMLInputElement>) => {
    try {
      const json = JSON.parse(event.target.value)

      // set specific values in the form
      form.setFieldValue(`${prefix}.clientId`, json.client_id)
      form.setFieldValue(`${prefix}.clientEmail`, json.client_email)
      form.setFieldValue(`${prefix}.privateKey`, json.private_key)
      form.setFieldValue(`${prefix}.privateKeyId`, json.private_key_id)
      form.setFieldValue(`${prefix}.clientX509CertUrl`, json.client_x509_cert_url)
      form.setFieldValue(`${prefix}.projectId`, json.project_id)
    } catch (err) {
      return notify.error(`Invalid JSON key file: ${err}`)
    }
  }

  return (
    <StepWrapper>
      <StepContentHeader
        centered
        large
        title='Connect to BigQuery'
        description={'Let’s authenticate your BigQuery account'}
      />
      <ScrollableStepContent inset autoHeight>
        <Card padding='xl' className='mx-8 w-full max-w-4xl' childrenWrapperClassName='flex w-full flex-col gap-4'>
          <TextField
            ref={nameRef}
            name={`connectionName`}
            label='Name'
            description='Easily identify and reuse this connection to ingest additional tables.'
            form={form}
            maxLength={64}
            useNestedLabel
            validators={createOnChangeValidator(
              {
                pattern: /^(?!\s*$).{1,64}$/,
                message: 'The connection name must be between 1 and 64 characters long.',
              },
              true,
            )}
          />
          <TextField
            name={`${prefix}.datasetName`}
            label='Dataset Name'
            description='The BigQuery dataset containing the tables you want to ingest'
            form={form}
            useNestedLabel
            validators={lakehouseFlowFieldValidator({
              stepId: StepId.ConnectBigQuery,
              listenTo: ['source.connection'],
              validator: isRequiredValidator(null, connection === LakehouseConnectionTypeEnum.Bigquery),
            })}
          />

          <div className='flex w-full flex-col gap-4 pt-6'>
            <TextField
              name={'source.bigqueryConnectionString'}
              label='Content of service account JSON key file'
              placeholder='{ "type": "service_account", ... }'
              description={
                <span>
                  Paste your complete Google Cloud service account JSON key file content.{' '}
                  <b>If provided, this will be used instead of the individual fields below.</b>
                </span>
              }
              form={form}
              useNestedLabel
              onBlur={parseJsonKeyFile}
            />
          </div>

          <div className='relative flex items-center gap-6 py-6'>
            <div className='flex-grow border-t border-slate-200' />
            <span className='h6'>Or Enter Details Individually</span>
            <div className='flex-grow border-t border-slate-200' />
          </div>

          <TextField
            name={`${prefix}.clientId`}
            label='Client ID'
            description='The unique identifier for the service account client (from client_id in JSON key file)'
            form={form}
            useNestedLabel
            validators={lakehouseFlowFieldValidator({
              stepId: StepId.ConnectBigQuery,
              listenTo: ['source.connection'],
              validator: isRequiredValidator(null, connection === LakehouseConnectionTypeEnum.Bigquery),
            })}
          />
          <TextField
            name={`${prefix}.clientEmail`}
            label='Client Email'
            description='The service account email address for authentication (from client_email in JSON key file)'
            form={form}
            useNestedLabel
            validators={lakehouseFlowFieldValidator({
              stepId: StepId.ConnectBigQuery,
              listenTo: ['source.connection'],
              validator: isRequiredValidator(null, connection === LakehouseConnectionTypeEnum.Bigquery),
            })}
          />
          <TextField
            name={`${prefix}.privateKeyId`}
            label='Private Key ID'
            description='The unique identifier for the private key (from private_key_id in JSON key file)'
            form={form}
            useNestedLabel
            validators={lakehouseFlowFieldValidator({
              stepId: StepId.ConnectBigQuery,
              listenTo: ['source.connection'],
              validator: isRequiredValidator(null, connection === LakehouseConnectionTypeEnum.Bigquery),
            })}
          />
          <TextField
            name={`${prefix}.privateKey`}
            label='Private Key'
            description='The private key for the service account in PEM format (from private_key in JSON key file)'
            form={form}
            useNestedLabel
            validators={lakehouseFlowFieldValidator({
              stepId: StepId.ConnectBigQuery,
              listenTo: ['source.connection'],
              validator: isRequiredValidator(null, connection === LakehouseConnectionTypeEnum.Bigquery),
            })}
          />
          <TextField
            name={`${prefix}.projectId`}
            label='Project ID'
            description='The Google Cloud Project ID containing your BigQuery datasets (from project_id in JSON key file)'
            form={form}
            useNestedLabel
            validators={lakehouseFlowFieldValidator({
              stepId: StepId.ConnectBigQuery,
              listenTo: ['source.connection'],
              validator: isRequiredValidator(null, connection === LakehouseConnectionTypeEnum.Bigquery),
            })}
          />
          <TextField
            name={`${prefix}.clientX509CertUrl`}
            label='Client x509 Cert Url'
            description='The URL for the X.509 certificate associated with the service account (from client_x509_cert_url in JSON key file)'
            form={form}
            useNestedLabel
            validators={lakehouseFlowFieldValidator({
              stepId: StepId.ConnectBigQuery,
              listenTo: ['source.connection'],
              validator: isRequiredValidator(null, connection === LakehouseConnectionTypeEnum.Bigquery),
            })}
          />
        </Card>
      </ScrollableStepContent>
      <form.Subscribe selector={(state) => [state.canSubmit, state.isPristine]}>
        {([canSubmit, isPristine]) => (
          <StepButtons
            context={context}
            isSubmitting={getTablesMutation.isPending}
            nextBlocked={getTablesMutation.isPending || !canSubmit || isPristine}
            hasCancel={true}
            onCancel={() => navigate('../', { relative: 'path' })}
            onNext={() => {
              const bigqueryConnectionStringPath = `${prefix}.bigqueryConnectionString`
              const bigqueryConnectionString = form.getFieldValue(bigqueryConnectionStringPath as any)

              const datasetNamePath = `${prefix}.datasetName`
              const datasetName = form.getFieldValue(datasetNamePath)

              const privateKeyPath = `${prefix}.privateKey`
              const privateKey = form.getFieldValue(privateKeyPath)

              const clientIdPath = `${prefix}.clientId`
              const clientId = form.getFieldValue(clientIdPath)

              const clientX509CertUrlPath = `${prefix}.clientX509CertUrl`
              const clientX509CertUrl = form.getFieldValue(clientX509CertUrlPath)

              const projectIdPath = `${prefix}.projectId`
              const projectId = form.getFieldValue(projectIdPath)

              const privateKeyIdPath = `${prefix}.privateKeyId`
              const privateKeyId = form.getFieldValue(privateKeyIdPath)

              const clientEmailPath = `${prefix}.clientEmail`
              const clientEmail = form.getFieldValue(clientEmailPath)

              const missingFields = [
                !clientEmail ? 'Client Email' : null,
                !clientId ? 'Client ID' : null,
                !clientX509CertUrl ? 'Client x509 Cert Url' : null,
                !privateKey ? 'Private Key' : null,
                !projectId ? 'Project ID' : null,
                !privateKeyId ? 'Private Key ID' : null,
                !datasetName ? 'Dataset Name' : null,
              ].filter(Boolean)

              if (!bigqueryConnectionString && missingFields && missingFields.length > 0) {
                return notify.error(`Please fill in all required fields, missing fields: ${missingFields.join(', ')}`)
              }

              if (typeof privateKey === 'string') {
                const cleanedPrivateKey = privateKey.replace(/\\n/g, '\n')
                form.setFieldValue(privateKeyPath, cleanedPrivateKey)
              }

              getTablesMutation.mutate()
            }}
          />
        )}
      </form.Subscribe>
    </StepWrapper>
  )
}
